import { Constants } from '@constants';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { fetchGetAccounts } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

type TAccessPeridStatus =
    | 'OPEN_AVAILABLE_FUNDS'
    | 'OPEN_EXPIRING_SOON'
    | 'OPEN_NO_FUNDS'
    | 'EXPIRED';

type TUseGetAccessPeriodBannerData = {
    accessPeriodStatus: TAccessPeridStatus;
    expiryDate: NonNullable<
        TAccount['formal_offer_access_period']
    >['valid_until'];
    availableCash: NonNullable<
        TAccount['formal_offer_access_period']
    >['available_cash'];
};

export const useGetAccessPeriodBannerData = (
    accountId: NonNullable<TAccount['account_id']>
): TUseGetAccessPeriodBannerData => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        queryKey: ['accounts', stateKey],
        queryFn: () => fetchGetAccounts({ stateKey }),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    if (!account) {
        throw new Error();
    }

    const accessPeriod = account.formal_offer_access_period;

    if (!accessPeriod) {
        throw new Error();
    }

    return {
        expiryDate: accessPeriod.valid_until,
        availableCash: accessPeriod.available_cash,
        accessPeriodStatus: getAccessPeriodStatus(accessPeriod)
    };
};

const getAccessPeriodStatus = (
    accessPeriod: NonNullable<TAccount['formal_offer_access_period']>
): TAccessPeridStatus => {
    if (isAccessPeriodValid(accessPeriod)) {
        if (accessPeriod.available_cash < Constants.MIN_DRAW_DOWN_AMOUNT) {
            return 'OPEN_NO_FUNDS';
        } else if (isAccessPeriodValidForSevenDaysOrLess(accessPeriod)) {
            return 'OPEN_EXPIRING_SOON';
        } else return 'OPEN_AVAILABLE_FUNDS';
    }
    return 'EXPIRED';
};

const isAccessPeriodValid = (
    accessPeriod: NonNullable<TAccount['formal_offer_access_period']>
): boolean => {
    return new Date(accessPeriod.valid_until) >= new Date();
};
const isAccessPeriodValidForSevenDaysOrLess = (
    accessPeriod: NonNullable<TAccount['formal_offer_access_period']>
): boolean => {
    const validUntilDate = new Date(accessPeriod.valid_until);
    const currentDate = new Date();
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(currentDate.getDate() + 7);

    // Ensure the valid_until date is after the current date and on or before seven days from now.
    return validUntilDate >= currentDate && validUntilDate <= sevenDaysFromNow;
};
